import { useState, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { User, Mail, Lock, Camera, Trash2, Save, AlertTriangle } from 'lucide-react'
import { userAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import DashboardHeader from '../components/DashboardHeader'
import DashboardFooter from '../components/DashboardFooter'
import TwoFactorSettings from '../components/TwoFactorSettings'

export default function Profile() {
  const [activeTab, setActiveTab] = useState<'profile' | 'email' | 'password' | 'security' | 'danger'>('profile')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const logout = useAuthStore((state) => state.logout)

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
  })

  // Email form state
  const [emailForm, setEmailForm] = useState({
    new_email: '',
    password: '',
  })

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  })

  // Delete account form state
  const [deleteForm, setDeleteForm] = useState({
    password: '',
    confirmation: '',
  })

  // Fetch user profile
  const { data: profile, isLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const { data } = await userAPI.getProfile()
      setProfileForm({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
      })
      return data
    },
  })

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      await userAPI.updateProfile(profileForm)
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
      toast.success('Profile updated successfully!')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update profile')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB')
      return
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file')
      return
    }

    setIsSubmitting(true)

    try {
      await userAPI.uploadAvatar(file)
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
      toast.success('Avatar updated successfully!')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to upload avatar')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      await userAPI.changeEmail(emailForm)
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
      toast.success('Email updated successfully!')
      setEmailForm({ new_email: '', password: '' })
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to change email')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(false)

    try {
      await userAPI.changePassword(passwordForm)
      toast.success('Password changed successfully!')
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' })
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to change password')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!confirm('Are you absolutely sure you want to delete your account? This action cannot be undone.')) {
      return
    }

    setIsSubmitting(true)

    try {
      await userAPI.deleteAccount(deleteForm)
      toast.success('Account deleted successfully')
      logout()
      navigate('/login')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete account')
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--color-primary)' }}></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--color-background)' }}>
      <DashboardHeader />
      
      <div className="flex-1 py-8 px-4">
        <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            Account Settings
          </h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Manage your account settings and preferences
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'profile'
                ? 'border-b-2'
                : ''
            }`}
            style={{
              borderColor: activeTab === 'profile' ? 'var(--color-primary)' : 'transparent',
              color: activeTab === 'profile' ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            }}
          >
            <User size={18} className="inline mr-2" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'email'
                ? 'border-b-2'
                : ''
            }`}
            style={{
              borderColor: activeTab === 'email' ? 'var(--color-primary)' : 'transparent',
              color: activeTab === 'email' ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            }}
          >
            <Mail size={18} className="inline mr-2" />
            Email
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'password'
                ? 'border-b-2'
                : ''
            }`}
            style={{
              borderColor: activeTab === 'password' ? 'var(--color-primary)' : 'transparent',
              color: activeTab === 'password' ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            }}
          >
            <Lock size={18} className="inline mr-2" />
            Password
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'security'
                ? 'border-b-2'
                : ''
            }`}
            style={{
              borderColor: activeTab === 'security' ? 'var(--color-primary)' : 'transparent',
              color: activeTab === 'security' ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            }}
          >
            <Lock size={18} className="inline mr-2" />
            Security (2FA)
          </button>
          <button
            onClick={() => setActiveTab('danger')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'danger'
                ? 'border-b-2 border-red-500'
                : ''
            }`}
            style={{
              color: activeTab === 'danger' ? '#ef4444' : 'var(--color-text-secondary)',
            }}
          >
            <AlertTriangle size={18} className="inline mr-2" />
            Danger Zone
          </button>
        </div>

        {/* Content */}
        <div className="p-6 rounded-lg shadow-sm" style={{ backgroundColor: 'var(--color-surface)' }}>
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div>
              <h2 className="text-xl font-semibold mb-6" style={{ color: 'var(--color-text-primary)' }}>
                Profile Information
              </h2>

              {/* Avatar Section */}
              <div className="mb-8 flex items-center gap-6">
                <div className="relative">
                  <div 
                    className="w-24 h-24 rounded-full flex items-center justify-center text-2xl font-bold"
                    style={{ 
                      backgroundColor: 'var(--color-primary)',
                      color: 'white'
                    }}
                  >
                    {profile?.avatar_url ? (
                      <img 
                        src={profile.avatar_url} 
                        alt="Avatar" 
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      (profile?.first_name?.[0] || profile?.email?.[0] || 'U').toUpperCase()
                    )}
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 p-2 rounded-full shadow-lg"
                    style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
                    disabled={isSubmitting}
                  >
                    <Camera size={16} />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-lg" style={{ color: 'var(--color-text-primary)' }}>
                    {profile?.first_name && profile?.last_name 
                      ? `${profile.first_name} ${profile.last_name}` 
                      : 'No name set'}
                  </h3>
                  <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                    {profile?.email}
                  </p>
                </div>
              </div>

              {/* Profile Form */}
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                      First Name
                    </label>
                    <input
                      type="text"
                      value={profileForm.first_name}
                      onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border"
                      style={{ 
                        backgroundColor: 'var(--color-background)',
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-primary)'
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={profileForm.last_name}
                      onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border"
                      style={{ 
                        backgroundColor: 'var(--color-background)',
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-primary)'
                      }}
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 rounded-lg font-medium text-white flex items-center gap-2"
                    style={{ backgroundColor: 'var(--color-primary)' }}
                  >
                    <Save size={18} />
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Email Tab */}
          {activeTab === 'email' && (
            <div>
              <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Change Email
              </h2>
              <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
                Update your email address. You'll need to verify your password.
              </p>

              <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: 'var(--color-background)' }}>
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                  Current Email
                </p>
                <p className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  {profile?.email}
                </p>
              </div>

              <form onSubmit={handleEmailChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                    New Email Address
                  </label>
                  <input
                    type="email"
                    value={emailForm.new_email}
                    onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--color-background)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)'
                    }}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={emailForm.password}
                    onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--color-background)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)'
                    }}
                    required
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 rounded-lg font-medium text-white"
                    style={{ backgroundColor: 'var(--color-primary)' }}
                  >
                    {isSubmitting ? 'Updating...' : 'Update Email'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <div>
              <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Change Password
              </h2>
              <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
                Ensure your account is using a strong password to stay secure.
              </p>

              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--color-background)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)'
                    }}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--color-background)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)'
                    }}
                    required
                    minLength={8}
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Minimum 8 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--color-background)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)'
                    }}
                    required
                    minLength={8}
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 rounded-lg font-medium text-white"
                    style={{ backgroundColor: 'var(--color-primary)' }}
                  >
                    {isSubmitting ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Security Tab - 2FA */}
          {activeTab === 'security' && (
            <div>
              <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Two-Factor Authentication (2FA)
              </h2>
              <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
                Add an extra layer of security to your account by enabling two-factor authentication.
              </p>
              
              <TwoFactorSettings />
            </div>
          )}

          {/* Danger Zone Tab */}
          {activeTab === 'danger' && (
            <div>
              <h2 className="text-xl font-semibold mb-2 text-red-600">
                Danger Zone
              </h2>
              <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
                Once you delete your account, there is no going back. Please be certain.
              </p>

              <div className="p-4 border-2 border-red-300 bg-red-50 rounded-lg mb-6">
                <h3 className="font-semibold text-red-900 mb-2">⚠️ Warning</h3>
                <ul className="list-disc list-inside text-sm text-red-800 space-y-1">
                  <li>All your data will be permanently deleted</li>
                  <li>Your dashboards, widgets, and settings will be lost</li>
                  <li>Your email address will be released</li>
                  <li>This action cannot be undone</li>
                </ul>
              </div>

              <form onSubmit={handleDeleteAccount} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={deleteForm.password}
                    onChange={(e) => setDeleteForm({ ...deleteForm, password: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--color-background)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)'
                    }}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                    Type "DELETE MY ACCOUNT" to confirm
                  </label>
                  <input
                    type="text"
                    value={deleteForm.confirmation}
                    onChange={(e) => setDeleteForm({ ...deleteForm, confirmation: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{ 
                      backgroundColor: 'var(--color-background)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)'
                    }}
                    placeholder="DELETE MY ACCOUNT"
                    required
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting || deleteForm.confirmation !== 'DELETE MY ACCOUNT'}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Trash2 size={18} />
                    {isSubmitting ? 'Deleting...' : 'Delete My Account'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Back to Dashboard Button */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm"
            style={{ color: 'var(--color-primary)' }}
          >
            ← Back to Dashboard
          </button>
        </div>
        </div>
      </div>

      {/* Footer */}
      <DashboardFooter />
    </div>
  )
}


